import { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import MosqueOutlinedIcon from '@mui/icons-material/MosqueOutlined';
import Brightness5OutlinedIcon from '@mui/icons-material/Brightness5Outlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import config from '../config/config.json';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const sectionCardSx = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
};

const periodLabels = {
  week: 'Bu hafta',
  month: 'Bu ay',
  year: 'Bu yıl',
};

const toneStyles = {
  primary: { backgroundColor: '#EFF6FF', valueColor: '#2563EB' },
  success: { backgroundColor: '#ECFDF5', valueColor: '#059669' },
  warning: { backgroundColor: '#FFF7ED', valueColor: '#D97706' },
  danger: { backgroundColor: '#FEF2F2', valueColor: '#DC2626' },
  neutral: { backgroundColor: '#F8FAFC', valueColor: '#475569' },
};

function SummaryMetric({ label, value, caption, tone = 'primary' }) {
  const palette = toneStyles[tone] || toneStyles.primary;

  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: '14px',
        backgroundColor: palette.backgroundColor,
        border: '1px solid #E5E7EB',
      }}
    >
      <Typography sx={{ color: '#6B7280', fontSize: '13px', fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '28px', lineHeight: 1.1, fontWeight: 800, color: palette.valueColor }}>
        {value}
      </Typography>
      {caption ? (
        <Typography sx={{ color: '#94A3B8', fontSize: '12px', mt: 0.75 }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function SectionShell({ title, description, action, children }) {
  return (
    <Box sx={sectionCardSx}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ color: '#6B7280' }}>{description}</Typography>
        </Box>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function SectionState({ loading, error, empty, emptyText = 'Bu aralıkta veri bulunmuyor.' }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (empty) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '16px',
          border: '1px dashed #CBD5E1',
          backgroundColor: '#F8FAFC',
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: '#64748B', fontWeight: 500 }}>{emptyText}</Typography>
      </Box>
    );
  }

  return null;
}

function ReportsPage() {
  const { token } = useAuth();
  const [prayerPeriod, setPrayerPeriod] = useState('week');
  const [fastingPeriod, setFastingPeriod] = useState('week');
  const [prayerStats, setPrayerStats] = useState(null);
  const [fastingStats, setFastingStats] = useState(null);
  const [kazaStats, setKazaStats] = useState(null);
  const [prayerLoading, setPrayerLoading] = useState(true);
  const [fastingLoading, setFastingLoading] = useState(true);
  const [kazaLoading, setKazaLoading] = useState(true);
  const [prayerError, setPrayerError] = useState('');
  const [fastingError, setFastingError] = useState('');
  const [kazaError, setKazaError] = useState('');

  const fetchPrayerStats = useCallback(async () => {
    try {
      setPrayerLoading(true);
      setPrayerError('');
      const response = await axios.get(`${config.api_base}/api/admin/reports/prayers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: prayerPeriod },
      });

      if (response.data.statusCode === 200) {
        setPrayerStats(response.data.data);
      } else {
        setPrayerError('Namaz raporları alınamadı.');
      }
    } catch (error) {
      console.error('Prayer stats error:', error);
      setPrayerError(error?.response?.data?.message || 'Namaz raporları alınamadı.');
    } finally {
      setPrayerLoading(false);
    }
  }, [token, prayerPeriod]);

  const fetchFastingStats = useCallback(async () => {
    try {
      setFastingLoading(true);
      setFastingError('');
      const response = await axios.get(`${config.api_base}/api/admin/reports/fasting`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: fastingPeriod },
      });

      if (response.data.statusCode === 200) {
        setFastingStats(response.data.data);
      } else {
        setFastingError('Oruç raporları alınamadı.');
      }
    } catch (error) {
      console.error('Fasting stats error:', error);
      setFastingError(error?.response?.data?.message || 'Oruç raporları alınamadı.');
    } finally {
      setFastingLoading(false);
    }
  }, [token, fastingPeriod]);

  const fetchKazaStats = useCallback(async () => {
    try {
      setKazaLoading(true);
      setKazaError('');
      const response = await axios.get(`${config.api_base}/api/admin/reports/kaza`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.statusCode === 200) {
        setKazaStats(response.data.data);
      } else {
        setKazaError('Kaza raporları alınamadı.');
      }
    } catch (error) {
      console.error('Kaza stats error:', error);
      setKazaError(error?.response?.data?.message || 'Kaza raporları alınamadı.');
    } finally {
      setKazaLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPrayerStats();
  }, [fetchPrayerStats]);

  useEffect(() => {
    fetchFastingStats();
  }, [fetchFastingStats]);

  useEffect(() => {
    fetchKazaStats();
  }, [fetchKazaStats]);

  const prayerDistribution = prayerStats?.distribution || [];
  const fastingTrend = fastingStats?.trend || [];
  const topPrayerUsers = prayerStats?.topUsers || [];
  const topDebtors = kazaStats?.topDebtors || [];

  const totalReportRecords =
    (prayerStats?.totalPrayers || 0) +
    (fastingStats?.totalFastings || 0) +
    (kazaStats?.totalDebt || 0);

  const pageSummaryChips = [
    {
      icon: <InsightsOutlinedIcon />,
      label: `${totalReportRecords} toplam kayıt`,
      sx: { backgroundColor: '#EFF6FF', color: '#2563EB' },
    },
    {
      icon: <MosqueOutlinedIcon />,
      label: `${prayerStats?.totalPrayers || 0} namaz`,
      sx: { backgroundColor: '#ECFDF5', color: '#059669' },
    },
    {
      icon: <Brightness5OutlinedIcon />,
      label: `${fastingStats?.totalFastings || 0} oruç`,
      sx: { backgroundColor: '#FFF7ED', color: '#C2410C' },
    },
    {
      icon: <AutorenewOutlinedIcon />,
      label: `${kazaStats?.totalRemaining || 0} kalan rekat`,
      sx: { backgroundColor: '#FEF2F2', color: '#B91C1C' },
    },
    {
      icon: <Groups2OutlinedIcon />,
      label: `${prayerStats?.activeUsers || 0} aktif namaz kullanıcısı`,
      sx: { backgroundColor: '#F8FAFC', color: '#475569' },
    },
  ];

  return (
    <Box>
      <Box sx={{ ...sectionCardSx, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontSize: '30px', fontWeight: 700, color: '#111827', mb: 1 }}>
              Raporlar
            </Typography>
            <Typography sx={{ color: '#6B7280', maxWidth: 720 }}>
              Namaz, oruç ve kaza verilerini aynı panel içinde karşılaştır; yoğunluğu, aktif kullanıcıyı ve borç yükünü daha hızlı tara.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            {pageSummaryChips.map((chip) => (
              <Chip
                key={chip.label}
                icon={chip.icon}
                label={chip.label}
                sx={{
                  ...chip.sx,
                  fontWeight: 600,
                  borderRadius: '10px',
                  height: 36,
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      <Stack spacing={3}>
        <SectionShell
          title="Namaz İstatistikleri"
          description={`Dağılım, aktif kullanıcı ve öne çıkan hesaplar · ${periodLabels[prayerPeriod]}`}
          action={
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={prayerPeriod} onChange={(event) => setPrayerPeriod(event.target.value)}>
                <MenuItem value="week">Bu Hafta</MenuItem>
                <MenuItem value="month">Bu Ay</MenuItem>
                <MenuItem value="year">Bu Yıl</MenuItem>
              </Select>
            </FormControl>
          }
        >
          <SectionState
            loading={prayerLoading}
            error={prayerError}
            empty={!prayerLoading && !prayerError && prayerDistribution.every((entry) => Number(entry.count || 0) === 0)}
            emptyText="Seçilen aralıkta namaz dağılım verisi bulunmuyor."
          />

          {!prayerLoading && !prayerError && prayerDistribution.some((entry) => Number(entry.count || 0) > 0) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.2fr 0.8fr' }, gap: 3 }}>
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '16px',
                  p: 2,
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Typography sx={{ fontWeight: 700, color: '#111827', mb: 2 }}>Vakit Dağılımı</Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={prayerDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={95}
                      dataKey="count"
                    >
                      {prayerDistribution.map((entry, index) => (
                        <Cell key={`${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Stack spacing={2.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <SummaryMetric label="Toplam namaz" value={prayerStats?.totalPrayers || 0} caption="Seçilen periyot" tone="warning" />
                  <SummaryMetric label="Aktif kullanıcı" value={prayerStats?.activeUsers || 0} caption="En az bir kayıt" tone="success" />
                </Box>

                <Box
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '16px',
                    p: 2,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#111827', mb: 2 }}>En Çok Namaz Kılanlar</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                          <TableCell sx={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Kullanıcı</TableCell>
                          <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Namaz</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topPrayerUsers.slice(0, 5).map((user) => (
                          <TableRow key={user.userId} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '13px' }}>
                                {user.username || user.email}
                              </Typography>
                              <Typography sx={{ color: '#6B7280', fontSize: '12px' }}>{user.email}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: '#2563EB', fontSize: '13px' }}>
                              {user.prayerCount}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Stack>
            </Box>
          )}
        </SectionShell>

        <SectionShell
          title="Oruç İstatistikleri"
          description={`Günlük trend ve katılım özeti · ${periodLabels[fastingPeriod]}`}
          action={
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={fastingPeriod} onChange={(event) => setFastingPeriod(event.target.value)}>
                <MenuItem value="week">Bu Hafta</MenuItem>
                <MenuItem value="month">Bu Ay</MenuItem>
                <MenuItem value="year">Bu Yıl</MenuItem>
              </Select>
            </FormControl>
          }
        >
          <SectionState
            loading={fastingLoading}
            error={fastingError}
            empty={!fastingLoading && !fastingError && fastingTrend.length === 0}
            emptyText="Seçilen aralıkta oruç trend verisi bulunmuyor."
          />

          {!fastingLoading && !fastingError && fastingTrend.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.4fr 0.6fr' }, gap: 3 }}>
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '16px',
                  p: 2,
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Typography sx={{ fontWeight: 700, color: '#111827', mb: 2 }}>Günlük Oruç Trendi</Typography>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={fastingTrend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10B981" radius={[8, 8, 0, 0]} name="Oruç Sayısı" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Stack spacing={2}>
                <SummaryMetric label="Toplam oruç" value={fastingStats?.totalFastings || 0} caption="Seçilen periyot" tone="success" />
                <SummaryMetric label="Aktif kullanıcı" value={fastingStats?.activeUsers || 0} caption="En az bir oruç kaydı" tone="primary" />
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>Trend Yorumu</Typography>
                  <Typography sx={{ color: '#6B7280', lineHeight: 1.7 }}>
                    Bu görünüm, seçilen aralıkta hangi günlerde katılımın yükseldiğini hızlıca görmeni sağlar. Ani düşüşler kullanıcı alışkanlığındaki kırılmaları işaret eder.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Chip label={`${fastingTrend.length} veri günü`} size="small" sx={{ backgroundColor: '#F8FAFC', color: '#475569' }} />
                    <Chip label={`${fastingStats?.activeUsers || 0} aktif kişi`} size="small" sx={{ backgroundColor: '#EFF6FF', color: '#2563EB' }} />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}
        </SectionShell>

        <SectionShell
          title="Kaza Borcu İstatistikleri"
          description="Toplam yük, ödenen rekat ve en yüksek borçlu kullanıcılar"
          action={
            <Chip
              icon={<TrendingUpOutlinedIcon />}
              label={`${kazaStats?.totalUsers || 0} kullanıcıda kayıt var`}
              sx={{
                backgroundColor: '#F8FAFC',
                color: '#475569',
                fontWeight: 600,
                borderRadius: '10px',
                height: 36,
              }}
            />
          }
        >
          <SectionState
            loading={kazaLoading}
            error={kazaError}
            empty={!kazaLoading && !kazaError && !kazaStats}
            emptyText="Kaza borcu verisi bulunmuyor."
          />

          {!kazaLoading && !kazaError && kazaStats && (
            <Stack spacing={3}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <SummaryMetric label="Toplam borç" value={kazaStats.totalDebt || 0} caption="Rekat" tone="warning" />
                <SummaryMetric label="Kılınan" value={kazaStats.totalPaid || 0} caption="Rekat" tone="success" />
                <SummaryMetric label="Kalan borç" value={kazaStats.totalRemaining || 0} caption="Rekat" tone="danger" />
                <SummaryMetric label="Toplam kullanıcı" value={kazaStats.totalUsers || 0} caption="Borç kaydı olan" tone="primary" />
              </Box>

              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 2.5, borderBottom: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
                  <Typography sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>En Çok Borcu Olanlar</Typography>
                  <Typography sx={{ color: '#6B7280' }}>Borç yükü en yüksek kullanıcıları buradan hızlıca inceleyebilirsin.</Typography>
                </Box>

                <TableContainer>
                  <Table sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                        <TableCell sx={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Kullanıcı</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Kalan Borç</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Kılınan</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topDebtors.length > 0 ? (
                        topDebtors.map((debtor) => (
                          <TableRow key={debtor.userId} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '13px' }}>
                                {debtor.username || debtor.email}
                              </Typography>
                              <Typography sx={{ color: '#6B7280', fontSize: '12px' }}>{debtor.email}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 800, color: '#DC2626', fontSize: '13px' }}>
                                {debtor.totalRemaining}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 800, color: '#059669', fontSize: '13px' }}>
                                {debtor.totalPrayed}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 6, color: '#64748B' }}>
                            Borç kaydı bulunan kullanıcı listesi oluşmadı.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}
        </SectionShell>
      </Stack>
    </Box>
  );
}

export default ReportsPage;
